import Game from '../Game';
import DateHelper from '../Utility/DateHelper';
import IPlayerData from './IPlayerData';
import Inventory from './PlayerProperty/Other/Inventory';
import { PlayerProperty } from './PlayerProperty/PlayerProperty';
import Progress from './PlayerProperty/Progress/Progress';
import Settings from './PlayerProperty/Settings/Settings';
import Social from './PlayerProperty/Social/Social';
import Timers from './PlayerProperty/Timers/Timers';
import Levels from './PlayerProperty/Other/Levels';
import Fields from './PlayerProperty/Other/Fields';
import MaxWorld from './PlayerProperty/Other/MaxWorld';
import FTUX from './PlayerProperty/Other/FTUX';
import Tracking from './PlayerProperty/Other/Tracking';
import NumericProperty from './PlayerProperty/Other/NumericProperty';
import BoolProperty from './PlayerProperty/Other/BoolProperty';
import Environment from '../Utility/Environment';
import BotOptIn from './PlayerProperty/Other/BotOptIn';
import HasShortcut from 'Player/PlayerProperty/Other/HasShortcut';
import { IGameState } from '../State/Game/IGameState';
import { IPlayerState } from '../State/Player/IPlayerState';
import StarChestLevel from './PlayerProperty/Other/StarChestLevel';
import LuckyTurnips from './PlayerProperty/Other/LuckyTurnips';
import { report } from '../report';
import { fbPendingRequestCategorizer } from '../Utility/SDK/Drivers/FBHelpers';
import Quests from './PlayerProperty/Other/Quests';
import Pets from './PlayerProperty/Other/Pets';
import AnyProperty from './PlayerProperty/Other/AnyProperty';
import AdCampaign from './PlayerProperty/Other/AdCampaign';
import { INITIAL_PREMIUM_SPINS_AMOUNT } from '../Constants/Player';
import GDPRCompliance from "./PlayerProperty/Other/GDPRCompliance";
import Farm from './PlayerProperty/Other/Farm';
import EventQuests from './PlayerProperty/Other/EventQuests';
import ObjectProperty from './PlayerProperty/Other/ObjectProperty';
import ArrayProperty from './PlayerProperty/Other/ArrayProperty';

type PlayerProperties = {
    [P in keyof IPlayerState]: PlayerProperty<
        P,
        // FIXME: restrict to keyof IPlayerData somehow
        any
    >;
};

export let playerEndpoint: IPlayerData;

export default class DataManager {
    public get playerEndpointData(): IPlayerData {
        return playerEndpoint;
    }
    public set playerEndpointData(value: IPlayerData) {
        playerEndpoint = value;
    }
    private readonly properties: PlayerProperties;

    /**
     * This sub-set of properties will instantly trigger a data save when changed, instead of waiting for the interval.
     */
    private readonly forceSaveProps: ReadonlyArray<keyof PlayerProperties>;

    private isDirty: boolean = false;

    private isSaving: boolean = false;

    private isStarted: boolean = false;

    constructor() {
        this.properties = {
            settings: new Settings(),
            inventory: new Inventory(),
            progress: new Progress(),
            social: new Social(),
            timers: new Timers(),
            quests: new Quests(),
            eventQuests: new EventQuests(),
            petsState: new Pets(),
            farmState: new Farm(),
            unlockedBoosters: new AnyProperty('unlockedBoosters', 'unlockedBoosters', []),

            // Progress
            levels: new Levels(),
            fields: new Fields(),
            starChestLevel: new StarChestLevel(),
            petLevel: new NumericProperty('petLevel', 'v1_71_5_petLevel'),
            luckyTurnipsState: new LuckyTurnips(),
            maxWorld: new MaxWorld(),
            FTUX: new FTUX(),
            lastClaimedSpinLevel: new NumericProperty(
                'lastClaimedSpinLevel',
                'lastClaimedSpinLevel'
            ),
            
            // If player had spun first time the wheels
            spunFirstNormalWheel: new BoolProperty('spunFirstNormalWheel', 'spunFirstNormalWheel'),
            spunFirstDiamondWheel: new BoolProperty('spunFirstDiamondWheel', 'spunFirstDiamondWheel'),

            isFbInstantUnified: new BoolProperty('isFbInstantUnified', 'isFbInstantUnified'),

            tasks: null,
            FBEvents: new Tracking(),
            usedCheats: new BoolProperty('usedCheats', 'v1_51_0_usedCheats'),
            purchases: new NumericProperty('purchases', 'purchases'),
            realMoneySpent: new NumericProperty(
                'realMoneySpent',
                'v1_39_0_realMoneySpent'
            ),
            coinValueBoughtWithRealMoney: new NumericProperty(
                'coinValueBoughtWithRealMoney',
                'v1_39_0_coinValueBoughtWithRealMoney'
            ),
            sessionCount: new NumericProperty('sessionCount', 'sessionCount'),
            totalSessionCount: new NumericProperty(
                'totalSessionCount',
                'totalSessionCount'
            ),
            botOptIn: new BotOptIn(),
            hasShortcut: new HasShortcut(),
            lastSundayOfferPurchaseTimestamp: new NumericProperty(
                'lastSundayOfferPurchaseTimestamp',
                'v1_71_0_lastSundayOfferPurchaseTimestamp'
            ),
            attributionCampaignID: new AdCampaign(),
            premiumWheelResetLevel: new NumericProperty(
                'premiumWheelResetLevel',
                'premiumWheelResetLevel',
            ),
            premiumWheelSpinsAmount: new AnyProperty(
                'premiumWheelSpinsAmount',
                'premiumWheelSpinsAmount',
                INITIAL_PREMIUM_SPINS_AMOUNT,
            ),
            gdprCompliance: new GDPRCompliance(),
            forceNewUserDdnaEvent: new AnyProperty('forceNewUserDdnaEvent', 'forceNewUserDdnaEvent', false),
            productsPurchased: new ObjectProperty('productsPurchased', "v2_28_3_productsPurchased", {}),
            decoItemsRewarded: new ArrayProperty('decoItemsRewarded', 'v2_28_3_farm_items_rewarded', []),
            decoItemsSeen: new ArrayProperty('decoItemsSeen', 'v2_33_0_farm_items_seen', []),
        };

        this.forceSaveProps = [
            'inventory',
            'levels',
            'fields',
            'timers',
            'usedCheats',
            'purchases',
            'realMoneySpent',
            'coinValueBoughtWithRealMoney',
            'luckyTurnipsState',
        ];
    }

    public start() {
        if (this.isStarted) return;

        this.isStarted = true;
        window.setInterval(() => {
            try {
                const playerState = Game.store.getState().playerState;
                this.flush(playerState);
            } catch (e) {
                console.warn(e);
            }
        }, Game.config.dataSaveIntervalMS);
    }

    /**
     * Fetch all data from the SDK and store in memory.
     */
    public async restore() {
        // @ts-ignore
        this.playerEndpointData = await Game.sdk.restore() || {} as IPlayerData;
    }

    public restorePlayerStateFromEndpointData(_playerData?: IPlayerData): IPlayerState {
        const playerData: IPlayerData = _playerData || this.playerEndpointData;

        // prettier-ignore
        const state: IPlayerState = {
            inventory: this.properties.inventory.restore(playerData),
            progress: this.properties.progress.restore(playerData),
            settings: this.properties.settings.restore(playerData),
            social: this.properties.social.restore(playerData),
            timers: this.properties.timers.restore(playerData),
            quests: this.properties.quests.restore(playerData),
            eventQuests: this.properties.eventQuests.restore(playerData),
            petsState: this.properties.petsState.restore(playerData),
            farmState: this.properties.farmState.restore(playerData),
            unlockedBoosters: this.properties.unlockedBoosters.restore(playerData),
            premiumWheelResetLevel: this.properties.premiumWheelResetLevel.restore(playerData),
            premiumWheelSpinsAmount: this.properties.premiumWheelSpinsAmount.restore(playerData),

            // Main Progress
            levels: this.properties.levels.restore(playerData),
            fields: this.properties.fields.restore(playerData),
            starChestLevel: this.properties.starChestLevel.restore(playerData),
            petLevel: this.properties.petLevel.restore(playerData),
            luckyTurnipsState: this.properties.luckyTurnipsState.restore(playerData),
            maxWorld: this.properties.maxWorld.restore(playerData),
            FTUX: this.properties.FTUX.restore(playerData),
            lastClaimedSpinLevel: this.properties.lastClaimedSpinLevel.restore(playerData),

            spunFirstNormalWheel: this.properties.spunFirstNormalWheel.restore(playerData),
            spunFirstDiamondWheel: this.properties.spunFirstDiamondWheel.restore(playerData),

            // FB unified
            isFbInstantUnified: this.properties.isFbInstantUnified.restore(playerData),

            // Misc Progress
            tasks: [],
            FBEvents: this.properties.FBEvents.restore(playerData),
            usedCheats: this.properties.usedCheats.restore(playerData),
            purchases: this.properties.purchases.restore(playerData),
            realMoneySpent: this.properties.realMoneySpent.restore(playerData),
            coinValueBoughtWithRealMoney: this.properties.coinValueBoughtWithRealMoney.restore(playerData),
            sessionCount: this.properties.sessionCount.restore(playerData),
            totalSessionCount: this.properties.totalSessionCount.restore(playerData),
            botOptIn: this.properties.botOptIn.restore(playerData),
            hasShortcut: this.properties.hasShortcut.restore(playerData),
            lastSundayOfferPurchaseTimestamp: this.properties.lastSundayOfferPurchaseTimestamp.restore(playerData),
            attributionCampaignID: this.properties.attributionCampaignID.restore(playerData),
            gdprCompliance: this.properties.gdprCompliance.restore(playerData),
            forceNewUserDdnaEvent: this.properties.forceNewUserDdnaEvent.restore(playerData),
            productsPurchased: this.properties.productsPurchased.restore(playerData),
            decoItemsRewarded: this.properties.decoItemsRewarded.restore(playerData),
            decoItemsSeen: this.properties.decoItemsSeen.restore(playerData),
        };

        try {
            if (process) {
                if (process.env.NODE_ENV === Environment.DEVELOPMENT) {
                    console.info(state);
                }
            }
        } catch {}

        return state;
    }

    public handleStateChanged(state: IGameState) {
        if (this.isStarted) {
            if (
                this.forceSaveProps
                    .map(propKey => this.properties[propKey])
                    .some(
                        prop =>
                            prop &&
                            prop.isChanged(
                                state.playerState,
                                this.playerEndpointData
                            )
                    )
            ) {
                this.flush(state.playerState, true);
            } else if (!this.isDirty) {
                this.isDirty = this.checkIfDirty(state.playerState);
            }
        }
    }

    public checkIfDirty(playerStateData: IPlayerState): boolean {
        return Object.values(this.properties).some(
            prop =>
                prop && prop.isChanged(playerStateData, this.playerEndpointData)
        );
    }

    /**
     * Save any changed data in the specified player state to the SDK.
     * @param playerStateData The state object to compare against cached data to determine the change-set.
     * @param force Immediately save changes, ignore `isDirty` flag.
     */
    public async flush(playerStateData: IPlayerState, force: boolean = false) {
        // Do not save if there is a save already in progress.
        if (this.isSaving) {
            this.isDirty = this.checkIfDirty(playerStateData);
            return;
        }

        // Check if the data is dirty or the `force` flag is true.
        if (force === false && !this.isDirty) return;

        // Immediately set isDirty flag to false to allow for dirtyificiation to take
        // place during the next save process.
        this.isDirty = false;

        // Set isSaving flag to true to prevent multiple save processes from running at the same time.
        this.isSaving = true;

        // Object.assign + spread to merge partials.
        const saveData: Partial<IPlayerData> = Object.assign(
            {},
            ...Object.values(this.properties)
                .filter(
                    prop =>
                        prop &&
                        prop.isChanged(playerStateData, this.playerEndpointData)
                )
                .map(prop => prop.save(playerStateData))
        );

        // Set a timestamp for data-integrity checking.
        saveData.data_timestamp = DateHelper.now.toString();

        await Game.sdk.save(saveData).catch(error => {
            report('Failed to save', {
                error,
                once: true,
                sampleRate: 0.1,
                categorizers: [fbPendingRequestCategorizer('save')],
            });
        });
        await Game.sdk.flushSaveData().catch(error => {
            report('Failed to flush save data', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        });

        // Update playerEndpointData with changes so we know if anything changes again.
        this.playerEndpointData = {
            ...this.playerEndpointData,
            ...saveData,
        };

        this.isSaving = false;
    }
}
